import {FormControl, FormGroup} from '@angular/forms';

import {BaseControl} from 'appShared/classes/controls';

import {
    AddNewExchangeAgreementTypesSelector,
    AddNewExchangeCodeSelector,
    AddNewExchangeControls,
    AddNewExchangeNameSelector,
    AddNewExchangeReportEndDaySelector,
    AddNewExchangeReportStartDaySelector,
} from 'appAssets/descriptors';

import {ExchangeInputModel, ExchangeModel, ExchangeResponseInterface, HttpStatusCodesEnum, ServerResponse, SesService} from '@spa/api';
import {switchMap} from 'rxjs/internal/operators';
import {Helper} from 'appShared/classes';
import {ExchangeFormErrorStatesEnum} from 'appEntries/panel/enums';
import {BaseMarketDataForm} from 'appShared/forms';

export class AddExchangeForm extends BaseMarketDataForm {
    private model: ExchangeModel;
    private readonly agreementTypes: Record<string, string>;

    constructor(sesService: SesService, agreementTypes: Record<string, string>, model: ExchangeModel = null) {
        super(sesService);
        this.model = model;
        this.agreementTypes = agreementTypes;
    }

    private createExchange() {
        const name = this.formGroup.value[AddNewExchangeNameSelector];
        const code = this.formGroup.value[AddNewExchangeCodeSelector];
        const reportStartDay = this.formGroup.value[AddNewExchangeReportStartDaySelector];
        const reportEndDay = this.formGroup.value[AddNewExchangeReportEndDaySelector];
        const agreementTypes = this.formGroup.value[AddNewExchangeAgreementTypesSelector];
        const inputModel = new ExchangeInputModel(null, name, code, reportStartDay, reportEndDay, agreementTypes);

        return this.sesService.createExchange(inputModel)
        .pipe(switchMap(registerResponse => {
            if (registerResponse.status !== HttpStatusCodesEnum.Ok) {
                return Helper.errorsHelper().createErrorResponseObservable<ExchangeResponseInterface>(
                    registerResponse,
                    ExchangeFormErrorStatesEnum.CreateExchangeFailed
                );
            }

            return Helper.createSuccessResponseObservable<boolean>(true);

        }));
    }

    private updateExchange() {
        const name = this.formGroup.value[AddNewExchangeNameSelector];
        const code = this.formGroup.value[AddNewExchangeCodeSelector];
        const reportStartDay = this.formGroup.value[AddNewExchangeReportStartDaySelector];
        const reportEndDay = this.formGroup.value[AddNewExchangeReportEndDaySelector];
        const agreementTypes = this.formGroup.value[AddNewExchangeAgreementTypesSelector];
        const inputModel = new ExchangeInputModel(this.model.id, name, code, reportStartDay, reportEndDay, agreementTypes);

        return this.sesService.updateExchange(this.model.id, inputModel)
        .pipe(switchMap(registerResponse => {
            if (registerResponse.status !== HttpStatusCodesEnum.Ok) {
                return Helper.errorsHelper().createErrorResponseObservable<ExchangeResponseInterface>(
                    registerResponse,
                    ExchangeFormErrorStatesEnum.UpdateExchangeFailed
                );
            }

            return Helper.createSuccessResponseObservable<boolean>(true);

        }));
    }

    initForm(): void {

        this.formGroup = new FormGroup({});

        if (!this.model) {

            this.controls = AddNewExchangeControls(this.agreementTypes).map((control: BaseControl) => {
                this.formGroup.addControl(control.name, new FormControl(control.value, control.validators));

                return control;
            });

            return;
        }

        this.controls = AddNewExchangeControls(this.agreementTypes).map((control: BaseControl) => {
            switch (control.name) {
                case AddNewExchangeNameSelector:
                    this.formGroup.addControl(control.name, new FormControl(this.model.name, control.validators));
                    break;
                case AddNewExchangeCodeSelector:
                    this.formGroup.addControl(control.name, new FormControl(this.model.code, control.validators));
                    break;
                case AddNewExchangeReportStartDaySelector:
                    this.formGroup.addControl(control.name, new FormControl(this.model.reportStartDay, control.validators));
                    break;
                case AddNewExchangeReportEndDaySelector:
                    this.formGroup.addControl(control.name, new FormControl(this.model.reportEndDay, control.validators));
                    break;
                case AddNewExchangeAgreementTypesSelector:
                    this.formGroup.addControl(control.name, new FormControl(this.model.agreementTypes, control.validators));
                    break;
            }

            return control;
        });
    }

    createRequest(): ServerResponse<boolean> {
        if (!this.formGroup.dirty) {
            return null;
        }

        if (!this.model) {
            return this.createExchange();
        }

        return this.updateExchange();
    }
}
